import { createSelector } from '@ngrx/store';
import { Attachment, ClinicalNote, DocumentInstance, Visit } from 'src/app/types/aggregate-graph.types';
import { allAttachments, visitsByVisitNumberDesc } from '../patient-graph/patient-graph.selectors';
import { AppState } from '../reducers';

export const selectSelectedClinicalNoteId = (state: AppState) => state.patientClinicalNote.selectedClinicalNoteId;
export const selectClinicalNotes = (state: AppState) => state.patientGraph.clinicalNotes;
export const selectAllAttachmentsSelected = (state: AppState) => state.patientClinicalNote.allAttachmentsSelected;
export const selectDocumentInstances = (state: AppState) => state.patientGraph.documentInstances;
export const selectAttachmentUploads = (state: AppState) => state.patientClinicalNote.attachmentUploads;

export const selectedClinicalNoteId = createSelector(
	selectSelectedClinicalNoteId,
	(clinicalNoteId) => clinicalNoteId
);

export const selectedClinicalNote = createSelector(
	selectedClinicalNoteId,
	selectClinicalNotes,
	(clinicalNoteId, clinicalNotes) => clinicalNotes.find(cn => cn.id === clinicalNoteId)
);

export const selectedDocumentInstance = createSelector(
	selectedClinicalNote,
	selectDocumentInstances,
	(clinicalNote, documentInstances) => documentInstances.find(td => td.id === clinicalNote?.documentInstanceId)
);

export const allAttachmentsSelected = createSelector(
	selectAllAttachmentsSelected,
	(allSelected) => allSelected
);

export interface VisitClinicalNoteList {
	visit: Visit;
	clinicalNotes: ClinicalNote[];
}

export const visitClinicalNotes = createSelector(
	visitsByVisitNumberDesc,
	selectClinicalNotes,
	(visits, clinicalNotes): VisitClinicalNoteList[] => visits.map(visit => ({
		visit: visit,
		clinicalNotes: clinicalNotes.filter(cn => cn.visitId === visit.id).sort(sortClinicalNotesByCreatedDateAsc)
	}))
);

const sortClinicalNotesByCreatedDateAsc = (a: ClinicalNote, b: ClinicalNote) => a.createdDate.localeCompare(b.createdDate);

export const selectedClinicalNoteAttachments = createSelector(
	selectedClinicalNote,
	allAttachments,
	(clinicalNote, attachments) => attachments.filter(a => a.clinicalNoteId === clinicalNote?.id)
);

export const attachmentUploads = createSelector(
	selectAttachmentUploads,
	(attachmentUploads) => attachmentUploads
);

export interface CombinedClinicalNoteAttachment {
	id: string;
	name: string;
	loaded: number;
	total: number;
	attachment?: Attachment;
	createdDate: string;
}

export const selectedCombinedClinicalNoteAttachments = createSelector(
	selectedClinicalNoteAttachments,
	attachmentUploads,
	(attachments, uploads): CombinedClinicalNoteAttachment[] => {
		const attachmentsWithUploads: CombinedClinicalNoteAttachment[] = attachments.map(a => ({
			id: a.id,
			name: a.name,
			loaded: 0,
			total: 0,
			attachment: a,
			createdDate: a.createdDate
		}));
		const uploadsWithoutAttachment = uploads.filter(u => !attachments.some(a => a.id === u.id));
		return [...attachmentsWithUploads, ...uploadsWithoutAttachment].sort((a, b) => a.createdDate.localeCompare(b.createdDate));
	}
);

export interface SelectedClinicalNotePayload {
	clinicalNote: ClinicalNote;
	documentInstance: DocumentInstance;
	combinedClinicalNoteAttachments: CombinedClinicalNoteAttachment[];
}

export const selectedClinicalNotePayload = createSelector(
	selectedClinicalNote,
	selectedDocumentInstance,
	selectedCombinedClinicalNoteAttachments,
	(clinicalNote, documentInstance, combinedAttachments): SelectedClinicalNotePayload[] => {
		if (clinicalNote === undefined || documentInstance === undefined) return [];
		else return [{
			clinicalNote: clinicalNote,
			documentInstance: documentInstance,
			combinedClinicalNoteAttachments: combinedAttachments
		}];
	}
);